import { useTranslation } from 'react-i18next'
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton'
import { formatDate } from '../../../lib/datetime'
import { useActionDetail } from '../hooks/useActionDetail'
import ActionCompletionNote from './ActionCompletionNote'
import ActionDocumentsSection from './ActionDocumentsSection'
import { ActionNotesTable } from './ActionNotesSection'
import ActionStatusTag from './ActionStatusTag'
import ActionTerminCard from './ActionTerminCard'
import '../../minutes/minutes.css'
import '../../meetings/meetings.css'
import '../actions.css'

interface Props {
  /** Gösterilecek alt görevin kimliği. */
  actionId: number
  /** `false` iken hiçbir ağ isteği atılmaz ({@link MainTaskDetailView} ile aynı sözleşme). */
  active?: boolean
}

/**
 * <b>Alt Görev Detayı</b> — <b>salt-okuma</b> gövde (Toplantı Yönetimi görev detay pop-up'ı,
 * 02.10.2026). {@link MainTaskDetailView}'un alt görev karşılığıdır.
 *
 * <p><b>Bölümler ve sıraları Alt Görev Detay sayfasıyla AYNIDIR</b> ({@code SubActionDetailPage}):
 * hedef teslim tarihi kartı · bilgi ızgarası (No · Atanan · Durum · Görevi Oluşturan · Kapanma
 * Tarihi) · Başlık/Açıklama · Görev Notları · Tamamlanma Notu · Dokümanlar.
 *
 * <p><b>Neden sayfa pop-up içine konmadı:</b> {@code MainTaskViewDialog} javadoc'undaki gerekçenin
 * aynısı — sayfa alan düzenleme, statü değişimi, atama, not ve termin talebi akışlarını taşır; onları
 * ikinci bir bağlamda kapalı tutmak, tek bir kaçağın salt-okuma sözünü bozacağı onlarca koşul
 * demektir. Düzenleme gerekiyorsa kullanıcı alt görevi sayfasında açar.
 *
 * <p>Sözleşme {@link MainTaskDetailView} ile aynıdır: notlarda Düzenle/Sil yok, dokümanlarda yalnız
 * indirme, termin kartında talep butonu yok ({@code onRequestDeadlineUpdate} verilmez).
 */
export default function SubTaskDetailView({ actionId, active = true }: Readonly<Props>) {
  const { t } = useTranslation()
  const { data, isLoading, isError } = useActionDetail(active ? actionId : null)

  if (isLoading) {
    return <LoadingSkeleton />
  }
  if (isError) {
    return <p className="act-empty-hint">{t('actions.detail.status.error')}</p>
  }
  if (!data) {
    return null
  }

  return (
    <div className="mtg-detail act-maintask-view">
      <ActionTerminCard deadline={data.deadline} statusId={data.statusId} />

      <section className="minutes-section act-subinfo-section">
        <div className="minutes-section-head">
          <h2>{t('actions.detail.subActionPage.title')}</h2>
          {data.overdueLabel && (
            <span className="act-overdue-label">{t('actions.status.overdueLabel')}</span>
          )}
        </div>

        <dl className="mtg-info-grid">
          <div>
            <dt>{t('actions.columns.number')}</dt>
            <dd>{data.recordNumber ?? ''}</dd>
          </div>
          <div>
            <dt>{t('actions.form.assignee')}</dt>
            <dd>{data.assignees?.[0]?.fullName ?? ''}</dd>
          </div>
          <div>
            <dt>{t('actions.columns.status')}</dt>
            <dd>
              <ActionStatusTag statusId={data.statusId} name={data.statusName ?? ''} />
            </dd>
          </div>
          <div>
            <dt>{t('actions.detail.info.createdBy')}</dt>
            <dd>{data.createdByName ?? '—'}</dd>
          </div>
          {data.closureDate && (
            <div>
              <dt>{t('actions.detail.info.closureDate')}</dt>
              <dd>{formatDate(data.closureDate)}</dd>
            </div>
          )}
        </dl>

        {/* Başlık/Açıklama — düz metin (MainTaskDetailView ile aynı gerekçe: salt-okuma bir
            pop-up'ta devre dışı girdi kutuları "yazabilirim" izlenimi verir). */}
        <div className="act-desc-grid">
          <div className="act-field">
            <span className="ui-field-label">{t('actions.detail.subActions.name')}</span>
            <p className="act-readonly-text">{data.name}</p>
          </div>
          <div className="act-field">
            <span className="ui-field-label">{t('actions.detail.subActions.description')}</span>
            <p className="act-readonly-text">{data.description ?? '—'}</p>
          </div>
        </div>
      </section>

      {/* Notlar — canManageNotes=false → Düzenle/Sil kolonu yok; "Not Ekle" hiç yok. */}
      <section className="minutes-section">
        <div className="minutes-section-head">
          <h2>{t('actions.detail.notes.title')}</h2>
        </div>
        <ActionNotesTable
          notes={data.notes}
          canManageNotes={false}
          onEdit={noop}
          onDelete={noop}
        />
      </section>

      <ActionCompletionNote note={data.completionNote} />

      <ActionDocumentsSection actionId={actionId} documents={data.documents} editable={false} />
    </div>
  )
}

/** Salt-okuma bolumlerin zorunlu ama cagrilmayan geri-cagrilari. */
function noop() {}
